<template>
    <section id="receive-page">

        <!-- Persistent Fields ( Don't change as you receive packages and mail ) -->
        <div class="persistent-selection-fields">
            <!-- Datepicker -->
            <ClDatePicker
                v-model="models.receiveDateString"
                :max="models.maxDate"
                :rules="[ rules.required ]"
                label="Date*"
            />

            <!-- Carrier Selection -->
            <v-select
                v-model="models.selectedCarrier"
                :items="carrierOptions"
                itemText="friendly"
                placeholder="Carrier*"
                outlined
                dense
                hideDetails
                returnObject
                :loading="carrierListInFlight"
                required
            />

            <!-- Received Count -->
            <div class="session-counter">
                Packages: {{ totalPackage }} / Mail: {{ totalMail }}
            </div>

            <!-- Voidable Transaction -->
            <TheVoidableLastTransaction
                class="voidable-transaction"
                :itemTransaction="lastReceivedItemTransaction"
                :inFlight="inFlight"
                @voidedItem="voidLastTransaction"
            />
        </div>

        <!-- Tabs - Packages & Mail -->
        <v-card
            outlined
            class="receive-container tab-container"
        >
            <v-tabs v-model="activeTab">
                <v-tab>
                    Packages
                </v-tab>
                <v-tab>
                    Mail
                </v-tab>
            </v-tabs>
            <!-- Packages Tab -->
            <v-tabs-items v-model="activeTab" class="tab-views">
                <v-tab-item>
                    <TabPackages
                        ref="package"
                        :receiveDate="receiveDate"
                        :carrier="models.selectedCarrier"
                        :receiveInFlight="inFlight"
                        @newPackage="( pkg ) => addPackage( pkg )"
                        @trackingNumberValidation="( params ) => checkValidTrackingNumber( params )"
                        @defaultPackageCarrier="setDefaultPackageCarrier()"
                    />
                </v-tab-item>
                <v-tab-item>
                    <TabMail
                        ref="mail"
                        :receiveDate="receiveDate"
                        :carrier="models.selectedCarrier"
                        :receiveInFlight="inFlight"
                        @newMail="( mail ) => addMail( mail )"
                        @defaultMailCarrier="setDefaultMailCarrier()"
                    />
                </v-tab-item>
            </v-tabs-items>
        </v-card>

    </section>
</template>

<script>
// Vuex
import { mapGetters, mapState } from 'vuex';

// Api
import LogisticsApi from '@/api/LogisticsApi';

// Services
import LabelPrintingService from '@/services/LabelPrintingService';

// Utils
import DateUtil from '@/utils/DateUtil';
import ErrorUtil from '@/utils/ErrorUtil';
import AudioUtil, { soundNames } from '@/utils/AudioUtil';
import ValidationUtil from '@/utils/ValidationUtil';

// Components
import TabPackages from './receive/TabPackages';
import TabMail from './receive/TabMail';
import TheVoidableLastTransaction from './receive/TheVoidableLastTransaction';

export default {
    name: 'MailReceive',
    components: {
        TabPackages,
        TabMail,
        TheVoidableLastTransaction,
    },
    data() {
        return {
            models: {
                receiveDateString: DateUtil.formatDate( DateUtil.startOfToday() ),
                maxDate: DateUtil.formatDate( DateUtil.startOfToday() ),
                selectedCarrier: {},
            },
            rules: {
                ...ValidationUtil,
            },
            activeTab: 0, // Packages == 0, Mail == 1
            inFlight: false,
            totalPackage: 0,
            totalMail: 0,
            lastReceivedItemTransaction: {},

            // Used to allow the tracking number field to update the carrier field,
            // turned off until business decides they want it back
            autoUpdateSelectedCarrierByTrackingNumber: false,
        };
    },
    computed: {
        ...mapGetters( {
            websiteId: 'Website/id',
            carrierOptions: 'Mail/carriersList',
        } ),
        ...mapState( {
            jwtToken: ( state ) => state.User.jwtToken,
            userAccountId: ( state ) => state.User.accountId,
            carrierListInFlight: ( state ) => state.Mail.carriers.inFlight,
            mailTypes: ( state ) => state.Mail.mailTypes.list,
            loggedInLocation: ( state ) => state.User.selectedLocation,
        } ),

        /** @returns { Boolean } */
        carrierIsSelected() {
            return Object.values( this.models.selectedCarrier ).length > 0;
        },

        /** @returns { Boolean } */
        tabIsPackages() {
            return this.activeTab === 0;
        },
        /** @returns { Boolean } */
        tabIsMail() {
            return this.activeTab === 1;
        },
        /** @returns { Date } */
        receiveDate() {
            return new Date( this.models.receiveDateString );
        },
    },
    methods: {
        async addReceiveMail( mailItem ) {
            this.inFlight = true;
            try {
                const { data } = await LogisticsApi.addReceiveMail( {
                    jwtToken: this.jwtToken,
                    websiteId: this.websiteId,
                    userAccountId: this.userAccountId,
                    receiveDate: mailItem.receiveDate || null,
                    mailCarrierId: mailItem.mailCarrierId || null,
                    mailTypeId: mailItem.mailTypeId || null,
                    customerId: mailItem.customerId || null,
                    sender: mailItem.sender || null,
                    transactionNotes: mailItem.notes || null,
                    packageTrackingNumber: mailItem.trackingNumber || null,
                    mailQuantity: mailItem.quantity || null,
                    photos: mailItem.photos || null,
                } );

                // If we're on the 'Package' tab else 'Mail'
                if ( this.tabIsPackages ) {
                    this.$refs.package.clearPicture();
                    this.$refs.package.initialFocus();
                } else {
                    this.$refs.mail.initialFocus();
                }
                this.totalPackage += ( mailItem.mailTypeId === this.mailTypes.PCKG.id ? 1 : 0 );
                this.totalMail += ( mailItem.mailTypeId === this.mailTypes.MAIL.id ? 1 : 0 );

                this.lastReceivedItemTransaction = ( data.receivedMailItem && data.receiveTransaction )
                    ? { mailItem: data.receivedMailItem, transaction: data.receiveTransaction }
                    : {};

                const snackbar = {
                    message: 'Item Received!',
                    type: 'success',
                };
                let audio = soundNames.SUCCESS_SHORT;
                if ( data.labelImageUrl ) {
                    try {
                        await LabelPrintingService.printLabel( data.labelImageUrl );
                    } catch ( error ) {
                        audio = soundNames.FAILURE_SHORT;
                        snackbar.type = 'warning';
                        snackbar.message += ` ${ ErrorUtil.getMessageFromError( error ) }`;
                    }
                } else {
                    audio = soundNames.FAILURE_SHORT;
                    snackbar.message += ' Failed to get a label to print. Search for this item on the Search page, and reprint the label.';
                    snackbar.type = 'warning';
                }

                await AudioUtil.playSound( audio );
                await this.$store.dispatch( 'snackbar', {
                    ...snackbar,
                    duration: 5000,
                }, { root: true } );
            } catch ( error ) {
                const errorMessage = error
                    && error.response.data.message.toLowerCase().includes( 'no dynamic' )
                    ? `Dynamic Mail is not set up for ${ this.loggedInLocation.nameFriendly }. Enable Dynamic Mail in 'Settings'`
                    : error.response.data.message;
                await this.$store.dispatch( 'snackbar', {
                    message: errorMessage,
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
                let errMsg = ErrorUtil.getMessageFromError( error, 'Error receiving mail' );
                if ( error?.response?.data?.errorList[ 0 ]?.error ) {
                    errMsg = error.response.data.errorList[ 0 ].error;
                }
                await AudioUtil.playSound( soundNames.FAILURE_SHORT );
                await this.$store.dispatch( 'snackbar', {
                    message: errMsg,
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
            } finally {
                this.inFlight = false;
            }
        },

        // eslint-disable-next-line object-curly-newline
        addPackage( { recipient, sender, notes, trackingNumber, photos } ) {
            this.addReceiveMail( {
                receiveDate: this.models.receiveDateString,
                mailCarrierId: this.models.selectedCarrier.id,
                mailTypeId: this.mailTypes.PCKG.id,
                customerId: recipient.customerId,
                sender,
                notes,
                trackingNumber,
                photos,
            } );
        },
        // eslint-disable-next-line object-curly-newline
        addMail( { recipient, sender, notes, quantity } ) {
            this.addReceiveMail( {
                receiveDate: this.models.receiveDateString,
                mailCarrierId: this.models.selectedCarrier.id,
                mailTypeId: this.mailTypes.MAIL.id,
                customerId: recipient.customerId,
                sender,
                notes,
                quantity,
            } );
        },

        // eslint-disable-next-line no-unused-vars
        checkValidTrackingNumber( { valid, carrierFriendly, trackingNumber } ) {
            if ( this.autoUpdateSelectedCarrierByTrackingNumber ) {
                if ( valid ) {
                    if ( this.models.selectedCarrier.length > 0 ) {
                        if ( carrierFriendly.id !== this.models.selectedCarrier.id ) {
                            this.models.selectedCarrier = carrierFriendly;
                        }
                    } else {
                        this.models.selectedCarrier = carrierFriendly;
                    }
                } else {
                    this.models.selectedCarrier = this.carrierOptions.find( ( carrierOption ) => carrierOption.name === 'OTHER' );
                }
            }
        },
        setDefaultMailCarrier() {
            this.models.selectedCarrier = this.carrierOptions.find( ( carrierOption ) => carrierOption.name === 'USPS' );
        },
        setDefaultPackageCarrier() {
            this.models.selectedCarrier = {};
        },
        voidLastTransaction() {
            this.totalPackage -= ( this.lastReceivedItemTransaction.mailItem.mailTypeId === this.mailTypes.PCKG.id ? 1 : 0 );
            this.totalMail -= ( this.lastReceivedItemTransaction.mailItem.mailTypeId === this.mailTypes.MAIL.id ? 1 : 0 );
            this.lastReceivedItemTransaction = {};
        },
    },
};
</script>

<!-- Unscoped styles for this component and it's children -->
<style lang="scss">

</style>

<!-- Scoped styles for this component -->
<style lang="scss" scoped>
.receive-container {
    .tab-views {
        padding: 1rem;
    }
}

.persistent-selection-fields {
    display: grid;
    grid-template-columns: 1fr;
    column-gap: 1rem;
    row-gap: 1rem;

    align-items: center;
    justify-content: space-between;
    margin: 1rem 0;
    @include mixins-above(tablet) {
        grid-template-columns: 1fr 1fr;
    }
    @include mixins-above(desktop) {
        grid-template-columns: 1fr 1fr minmax(175px, 1fr) minmax(5rem, 1fr);
    }

    .voidable-transaction {
        grid-column: 1 / -1;
    }

    .back-button {
        justify-self: end;
    }
}
</style>
